import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Dayjs } from 'dayjs';
import Countries from 'frontend-utils/utils/countries';
import type { MembershipType } from 'lumen/utils/constants/general-constants';
import type { DimensionValue } from 'lumen/utils/constants/pi-constant-values';
import {
  DIMENSION_TRANSLATIONS,
  DIMENSIONS,
  DISCOUNT_LABELS,
  DISCOUNT_LABELS_KEYS,
  MEMBERSHIP_TYPES,
  TRANSLATIONS_MEMBERSHIP_TYPES,
  TRANSLATIONS_MOBILE_PROMOTIONS,
} from 'lumen/utils/constants/pi-constant-values';
import generateFakeName from 'lumen/utils/parity-analytics/pa-generate-fake-name';

type DiscountLabel = keyof typeof DISCOUNT_LABELS;

function isDiscountDimension(
  dimension: DimensionValue,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value: string,
): value is DiscountLabel {
  return dimension === DIMENSIONS.DISCOUNT;
}

const OBFUSCATED_DIMENSIONS: DimensionValue[] = [
  DIMENSIONS.MARKET,
  DIMENSIONS.BRAND,
  DIMENSIONS.HOTEL,
];

const MESSAGES = {
  FILTER_NO: 'filter.no',
  REFERENCE_MISSING: 'reference.missing',
} as const;

type Message = '' | (typeof MESSAGES)[keyof typeof MESSAGES];

interface BreakdownTooltipValues {
  difference: number;
  formattedCurrent: string;
  formattedDifference: string;
  formattedReference: string;
  isReferenceMissing: boolean;
  labelCurrentValue: string;
  labelReferenceValue: string;
}

export default class ParityStrategyBreakdown extends Model {
  [Type] = 'parity-strategy/breakdown' as const;

  @attr('string') declare dimension: DimensionValue;
  @attr('string') declare extractDate: string;
  @attr('number') declare lossScoreHotels: number;
  @attr('number') declare lossScoreShops: number;
  @attr('string') declare message: Message;
  @attr('number') declare referenceLossScoreHotels: number;
  @attr('number') declare referenceLossScoreShops: number;
  @attr('string') declare value: string;
  @attr('string') declare valueId: string;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare psSelections: Services['ps-selections'];

  private countries: Countries = new Countries(getOwner(this)!);

  modelName = 'parity-strategy/breakdown';

  /*
    This value is already calculated in the Prism::BulletGraph component to be shown as one of its values,
    but because it's needed for the tooltips we calculate it here as well
  */
  get differenceLossScore(): number {
    return this.referenceLossScore
      ? this.lossScore - this.referenceLossScore
      : 0;
  }

  get extractDateMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(this.extractDate);
  }

  get formattedDifferenceLossScore(): string {
    return this.intl.formatNumber(this.differenceLossScore, {
      style: 'percent',
      signDisplay: 'exceptZero',
    });
  }

  get formattedDimension(): string {
    return this.intl.t(DIMENSION_TRANSLATIONS[this.dimension], { count: 1 });
  }

  get formattedLossScore(): string {
    return this.intl.formatNumber(this.lossScore, {
      style: 'percent',
    });
  }

  get formattedReferenceLossScore(): string {
    return this.intl.formatNumber(this.referenceLossScore, {
      style: 'percent',
    });
  }

  get formattedValue(): string {
    if (
      this.hotelAndMarketService.isDemoModeActive &&
      OBFUSCATED_DIMENSIONS.includes(this.dimension)
    ) {
      return generateFakeName(this.value, this.dimension);
    }

    if (this.dimension === DIMENSIONS.MARKET) {
      if (this.value.startsWith('general.myhotels')) {
        return this.intl.t(this.value);
      }
    }

    if (this.dimension === DIMENSIONS.CHANNEL) {
      return this.ota.otaIdToPrettyName(this.value);
    }

    if (isDiscountDimension(this.dimension, this.value)) {
      if (this.value === DISCOUNT_LABELS_KEYS.LESS_THAN_5) {
        return this.intl.t(DISCOUNT_LABELS[this.value], { count: '5%' });
      }
      if (this.value === DISCOUNT_LABELS_KEYS.MORE_THAN_20) {
        return this.intl.t(DISCOUNT_LABELS[this.value], { count: '20%' });
      }
      return DISCOUNT_LABELS[this.value];
    }

    if (this.dimension === DIMENSIONS.MOBILE) {
      return this.intl.t(
        TRANSLATIONS_MOBILE_PROMOTIONS[
          this.value as keyof typeof TRANSLATIONS_MOBILE_PROMOTIONS
        ],
      );
    }

    if (this.dimension === DIMENSIONS.POS) {
      return this.countries.getName(this.value.toUpperCase());
    }

    if (this.dimension === DIMENSIONS.LOS) {
      return this.intl.t('rates.nights', { count: this.value });
    }

    if (this.dimension === DIMENSIONS.MEMBERSHIP) {
      if (
        this.value === MEMBERSHIP_TYPES.COUPON_DUPLICATE ||
        this.value === MEMBERSHIP_TYPES.MEMBER_DUPLICATE
      ) {
        return this.intl.t(TRANSLATIONS_MEMBERSHIP_TYPES[this.value] as string);
      }
      return this.intl.t(
        TRANSLATIONS_MEMBERSHIP_TYPES[
          parseInt(this.value, 10) as MembershipType
        ] as string,
      );
    }

    return this.value;
  }

  get formattedValueWithCustomValue(): string {
    return this.formattedValue;
  }

  get isReferenceMissing(): boolean {
    return this.message === MESSAGES.REFERENCE_MISSING;
  }

  get lossScore(): number {
    return this.psSelections.isHotelSelected
      ? this.lossScoreShops
      : this.lossScoreHotels;
  }

  // Integer values used for  Prism::BulletGraph
  get lossScoreAsInteger(): number | null {
    if (this.message === MESSAGES.FILTER_NO) {
      return null;
    }

    return Math.round(100 * this.lossScore);
  }

  get mappedValueId(): string {
    return this.valueId;
  }

  get referenceLossScore(): number {
    return this.psSelections.isHotelSelected
      ? this.referenceLossScoreShops
      : this.referenceLossScoreHotels;
  }

  get referenceLossScoreAsInteger(): number {
    return Math.round(100 * this.referenceLossScore);
  }

  get tooltipValues(): BreakdownTooltipValues {
    return {
      labelCurrentValue: this.intl.t('parity_analytics.tooltip.current_week'),
      labelReferenceValue: this.intl.t(
        'parity_analytics.tooltip.previous_week',
      ),
      formattedCurrent: this.formattedLossScore,
      formattedReference: this.formattedReferenceLossScore,
      formattedDifference: this.formattedDifferenceLossScore,
      difference: this.differenceLossScore,
      isReferenceMissing: this.isReferenceMissing,
    };
  }
}
